import React, { SetStateAction, useMemo } from "react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { ArchivedDocumentWithDetails, VisibleColumnSpec } from "@/types";
import { useTranslation } from "react-i18next";
import NonSortableHeader from "@/components/molecules/NonSortableHeader";
import DataTable from "@/components/molecules/DataTable";
import { useVisibleColumns } from "@/hooks/useVisibleColumns";
import { deleteDocument, useGetDocumentArchiveByCompanyId } from "@/services/Documents";
import { useAuth } from "@/contexts/AuthContext";
import { convertBytesToMb, getFileSizeInBytes } from "@/utils/FileUtils";
import Group from "@/components/atoms/Group";
import { Menu } from "@mantine/core";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import dayjs from "dayjs";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import Checkbox from "@/components/atoms/Checkbox";

interface DocumentTableProps {
  visibleColumns?: VisibleColumnSpec<ArchivedDocumentWithDetails, "checkbox" | "actions" | "size">[];
  initialSort?: SortingState;
  showDeactivated?: boolean;
  pagination?: boolean;
  actionable?: boolean;
  selectable?: boolean;
  initialRowsPerPage?: number;
  selectedRows?: string[];
  setSelectedRows?: (rows: string[]) => void | React.Dispatch<SetStateAction<string[]>>;
}

const DocumentTable: React.FC<DocumentTableProps> = ({
  visibleColumns,
  initialSort = null,
  showDeactivated = false,
  pagination = true,
  actionable = true,
  selectable = false,
  initialRowsPerPage = 15,
  selectedRows = [],
  setSelectedRows,
}) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const { data: documents, isLoading } = useGetDocumentArchiveByCompanyId(
    currentCompany?.companyId as number,
    showDeactivated,
    {
      enabled: !!currentCompany?.companyId,
    }
  );
  const queryClient = useQueryClient();

  const { mutateAsync: deleteDocumentMutation } = useMutation({
    mutationFn: async (uuid: string) => {
      return await deleteDocument(uuid, currentCompany?.companyId as number);
    },
    onSuccess: async (_) => {
      await queryClient.invalidateQueries({
        queryKey: ["getDocumentArchiveByCompanyId", currentCompany?.companyId],
      });
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.deleteSuccess"), t("notifications:document.deleteSuccess"))
      );
    },
    onError: async (_) => {
      await queryClient.invalidateQueries({
        queryKey: ["getDocumentArchiveByCompanyId", currentCompany?.companyId],
      });
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.deleteError"), t("notifications:document.deleteError"))
      );
    },
  });

  const baseColumns: ColumnDef<ArchivedDocumentWithDetails>[] = useMemo(
    () => [
      {
        header: () => <></>, // Empty header for the checkbox column
        id: "checkbox",
        size: 10,
        cell: ({ row }) => {
          // Function to handle row selection
          if (!selectedRows || !setSelectedRows) return <></>;

          const handleSelect = () => {
            if (!setSelectedRows) return; // Ensure setSelectedRows is defined

            const updatedRows = selectedRows.includes(row.original.id)
              ? selectedRows.filter((id) => id !== row.original.id)
              : [...selectedRows, row.original.id];

            // Narrow the type of setSelectedRows
            if (typeof setSelectedRows === "function") {
              // Case 1: setSelectedRows is a direct function
              setSelectedRows(updatedRows);
            } else {
              // Case 2: setSelectedRows is a React state updater
              (setSelectedRows as React.Dispatch<SetStateAction<string[]>>)((_) => updatedRows);
            }
          };
          const isChecked = selectedRows?.includes(row.original.id);

          if (!selectable) {
            return null;
          } else {
            return <Checkbox checked={isChecked} onChange={handleSelect} />;
          }
        },
      },
      {
        header: ({ column }) => NonSortableHeader({ column, label: t("common:name") }),
        id: "fileName",
        cell: ({ row }) => row.original.documentTitle,
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("dashboard:documentType")} />,
        id: "documentType",
        cell: ({ row }) => {
          const doc = row.original;
          return (
            <a
              download={row.original.documentTitle}
              href={`data:${doc.mediaType};base64,${doc.document}`}
              style={{
                color: doc.deleted ? "var(--color-text-secondary)" : "inherit",
              }}
            >
              {t("enumerables:documentTypes." + doc.documentType)}
            </a>
          );
        },
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("dashboard:fileType")} />,
        id: "fileType",
        cell: ({ row }) => row.original.fileType,
      },
      {
        header: ({ column }) => NonSortableHeader({ column, label: t("common:size") }),
        id: "size",
        accessorKey: "size",
        cell: ({ row }) => `${convertBytesToMb(getFileSizeInBytes(row.original.document))}MB`, // get the file size
      },
      {
        header: ({ column }) => NonSortableHeader({ column, label: t("common:date") }),
        id: "createdTime",
        cell: ({ row }) => dayjs(row.original.createdTime).format("DD.MM.YYYY"), // get the file size
      },
      {
        header: () => <></>,
        id: "actions",
        size: 50,
        minSize: 50,
        maxSize: 50,
        cell: ({ row }) => {
          if (!actionable) return null;
          return (
            <Group justify={"flex-end"} w={"100%"}>
              <Menu offset={0} position={"bottom-end"}>
                <Menu.Target>
                  <span style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                    <Group align={"center"} justify={"center"}>
                      <SVGAsset name="dotdotdot-grey" />
                    </Group>
                  </span>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    style={{ width: "auto" }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      await deleteDocument(row.original.id, currentCompany?.companyId as number);
                    }}
                  >
                    <a
                      download={row.original.documentTitle}
                      href={`data:${row.original.mediaType};base64,${row.original.document}`}
                    >
                      <Group>{t("actions:download")}</Group>
                    </a>
                  </Menu.Item>
                  {!row.original.deleted && (
                    <Menu.Item
                      style={{ width: "auto" }}
                      onClick={async (e) => {
                        e.stopPropagation();
                        await deleteDocumentMutation(row.original.id);
                      }}
                    >
                      <Group>{t("actions:deactivate")}</Group>
                    </Menu.Item>
                  )}
                </Menu.Dropdown>
              </Menu>
            </Group>
          );
        },
      },
    ],
    [t, selectedRows, setSelectedRows]
  );

  const columns = useVisibleColumns<ArchivedDocumentWithDetails, "checkbox" | "actions" | "size">(
    baseColumns,
    visibleColumns
  );

  const getRowClassName = (row: ArchivedDocumentWithDetails) => (row.deleted ? "disabled-row" : "");

  return (
    <>
      <DataTable
        onRowClick={undefined}
        data={documents || []}
        columns={columns}
        isLoading={isLoading}
        mobileHeaderCell={1}
        showPagination={pagination}
        initialSort={initialSort ?? undefined}
        initialRowsPerPage={initialRowsPerPage}
        rowClassName={getRowClassName}
      />
    </>
  );
};

export default DocumentTable;
